import { AssignmentStatus } from '../../types/assignment';
import { JobAttachment, JobComment, JobStatusChange } from './queries';
import type { ResultOf } from 'gql.tada';
import { HistoryItem } from '../Timeline/types';
import { IconType } from '../Icon/Icon';
import Icon from '../Icon/Icon';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import colors from '../../theme/colors';
import React, { useId } from 'react';
import { Box } from 'rebass';
import ActivityActionTooltip, {
  getActivityActionTooltipId,
} from '../ActivityFeed/ActivityActionTooltip';
import type { GET_TEST_REPORT } from '../BackflowTest/queries';
import type { GET_HAZARD_SURVEY_JOB } from '../WaterHazard/queries';
import type { GET_JOB as GET_RECYCLED_WATER_INSPECTION_JOB } from '../RecycledWater/RecycledWaterInspectionThirdPane';

type AttachmentFromJob<TJob> = TJob extends { attachments?: infer TAttachments }
  ? NonNullable<TAttachments> extends ReadonlyArray<infer TAttachment>
    ? TAttachment
    : never
  : never;

type BackflowTestJob = NonNullable<
  ResultOf<typeof GET_TEST_REPORT>['backflowTests'][number]['job']
>;
type HazardSurveyJob = ResultOf<typeof GET_HAZARD_SURVEY_JOB>['jobs'][number];
type RecycledWaterInspectionJob = ResultOf<
  typeof GET_RECYCLED_WATER_INSPECTION_JOB
>['jobs'][number];

export type JobHistoryAttachment =
  | JobAttachment
  | AttachmentFromJob<BackflowTestJob>
  | AttachmentFromJob<HazardSurveyJob>
  | AttachmentFromJob<RecycledWaterInspectionJob>;

export type JobHistoryAddress = {
  latitude?: number | null;
  longitude?: number | null;
} | null;

const WaterSupplierVisibilityIcon = ({
  waterSupplierName,
}: {
  waterSupplierName: string;
}) => {
  const tooltipId = getActivityActionTooltipId(useId());
  const tooltipText = `Visible to ${waterSupplierName}`;

  return React.createElement(
    Box,
    {
      'data-tooltip-id': tooltipId,
      'data-tooltip-content': tooltipText,
      'aria-label': tooltipText,
      sx: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        color: colors.base.gray[500],
      },
    },
    React.createElement(Icon, { icon: 'eye', size: '12' }),
    React.createElement(ActivityActionTooltip, { id: tooltipId })
  );
};

export const convertJobComment = (
  { id, created, profile, type, comment, visibility }: JobComment,
  waterSupplierName?: string | null,
  waterSupplierProfileId?: string | null
): HistoryItem<'COMMENT' | 'APPROVAL'> => {
  let title: string | undefined = undefined;
  let historyType: 'COMMENT' | 'APPROVAL' = 'COMMENT';
  let icon: IconType = 'chat-dots';
  let iconColor: string | undefined = undefined;
  let noRail: boolean = !!comment;

  if (['APPROVAL', 'REJECTION'].includes(type)) {
    historyType = 'APPROVAL';
    if (type === 'APPROVAL' && comment?.toUpperCase() === 'APPROVED') {
      title = 'Approved';
      icon = 'check';
      iconColor = colors.statusSuccess;
      noRail = false;
    } else {
      title = 'Rejected';
      icon = 'x';
      iconColor = colors.statusError;
      noRail = false;
    }
    if (['APPROVED', 'REJECTED'].includes(comment?.toUpperCase() ?? '')) {
      comment = '';
    }
  }

  return {
    id,
    created: created ?? 0,
    profile: profile,
    type: historyType,
    comment: comment || undefined,
    title,
    icon,
    iconColor,
    noRail,
    adornment:
      visibility === 'EVERYONE' &&
      waterSupplierName &&
      `${profile.id}` !== `${waterSupplierProfileId}`
        ? React.createElement(WaterSupplierVisibilityIcon, {
            waterSupplierName,
          })
        : undefined,
  };
};

export const getJobStatusIcon = (jobStatus: AssignmentStatus): IconType => {
  switch (jobStatus) {
    case 'NEW':
      return 'file';
    case 'ASSIGNED':
      return 'person';
    case 'CANCELED':
    case 'CANCELED_REASSIGNED':
      return 'x';
    case 'FOLLOW_UP':
      return 'repeat';
    case 'COMPLETED':
      return 'check';
    case 'SUBMITTED':
      return 'mail';
  }
  return 'edit';
};

export const convertJobStatusChange = ({
  id,
  created,
  profile,
  toStatus,
}: JobStatusChange): HistoryItem<'STATUS'> => ({
  id,
  created: created ?? 0,
  profile,
  type: 'STATUS',
  title: constantToLabel(toStatus),
  icon: getJobStatusIcon(toStatus),
});

export const convertJobAttachment = (
  attachment: JobHistoryAttachment,
  showAttachmentMetadata = false,
  jobAddress?: JobHistoryAddress
): HistoryItem<'ATTACHMENT'> => {
  const { id, created, profile, url, thumb, contentType } = attachment;
  const metadata =
    'source' in attachment
      ? {
          attachmentSource: attachment.source,
          takenAt: attachment.takenAt,
          latitude: attachment.latitude,
          longitude: attachment.longitude,
          jobLatitude: jobAddress?.latitude,
          jobLongitude: jobAddress?.longitude,
        }
      : {};

  return {
    id,
    created: created!,
    profile: profile!,
    type: 'ATTACHMENT',
    url,
    thumb: thumb ?? undefined,
    contentType: contentType ?? undefined,
    ...metadata,
    showAttachmentMetadata,
    icon: contentType?.startsWith('image/') ? 'image' : 'file',
    noRail: true,
  };
};
